import { Component, Input, Output, EventEmitter, computed } from '@angular/core';

import { cva } from '../utils/class-variance.util';
import { ThemeSection } from '../services/theme.service';
import { IconGlassComponent } from '../icon/icon-glass.component';
import { ChipVariant, ChipSize } from '../models/chip.model';

const chipVariants = cva({
  base: [
    'inline-flex items-center rounded-full font-medium transition-all duration-300 ease-out transform-gpu',
    'backdrop-blur-md border cursor-default',
    'hover:scale-105 active:scale-95',
    // Glass morphism base
    'before:content-[""] before:absolute before:inset-0',
    'before:bg-glass-gradient-light dark:before:bg-glass-gradient-dark',
    'before:opacity-60 before:rounded-full before:pointer-events-none',
    'before:transition-opacity before:duration-300',
  ].join(' '),
  variants: {
    variant: {
      default: [
        'bg-gray-100/60 text-gray-800 border-gray-200/50',
        'dark:bg-gray-800/60 dark:text-gray-200 dark:border-gray-700/50',
      ].join(' '),
      primary: [
        'bg-primary-100/60 text-primary-800 border-primary-200/50',
        'dark:bg-primary-900/60 dark:text-primary-200 dark:border-primary-800/50',
      ].join(' '),
      secondary: [
        'bg-gray-100/60 text-gray-700 border-gray-200/50',
        'dark:bg-gray-800/60 dark:text-gray-300 dark:border-gray-700/50',
      ].join(' '),
      success: [
        'bg-cyan-100/60 text-cyan-800 border-cyan-200/50',
        'dark:bg-cyan-900/60 dark:text-cyan-200 dark:border-cyan-800/50',
      ].join(' '),
      warning: [
        'bg-yellow-100/60 text-yellow-800 border-yellow-200/50',
        'dark:bg-yellow-900/60 dark:text-yellow-200 dark:border-yellow-800/50',
      ].join(' '),
      error: [
        'bg-red-100/60 text-red-800 border-red-200/50',
        'dark:bg-red-900/60 dark:text-red-200 dark:border-red-800/50',
      ].join(' '),
      info: [
        'bg-blue-100/60 text-blue-800 border-blue-200/50',
        'dark:bg-blue-900/60 dark:text-blue-200 dark:border-blue-800/50',
      ].join(' '),
      premium: [
        'bg-linear-to-r from-primary-100/60 via-purple-100/60 to-primary-100/60',
        'text-primary-800 border-primary-200/60',
        'dark:bg-linear-to-r dark:from-primary-900/60 dark:via-purple-900/60 dark:to-primary-900/60',
        'dark:text-primary-200 dark:border-primary-800/60',
      ].join(' '),
    },
    size: {
      xs: 'px-2 py-1 text-xs',
      sm: 'px-2.5 py-1.5 text-xs',
      md: 'px-3 py-2 text-sm',
      lg: 'px-4 py-2.5 text-base',
    },
    clickable: {
      true: 'cursor-pointer hover:shadow-lg min-h-11',
      false: 'cursor-default',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'md',
    clickable: 'false',
  },
});

const deleteButtonVariants = cva({
  base: [
    'ml-1.5 inline-flex items-center justify-center rounded-full',
    'transition-all duration-200 ease-out hover:scale-110 active:scale-90',
    'focus:outline-none focus:ring-2 focus:ring-offset-1',
    // 44px minimum touch target via pseudo-element
    'relative after:content-[""] after:absolute after:-inset-3 after:rounded-full',
  ].join(' '),
  variants: {
    variant: {
      default: [
        'text-gray-500 hover:text-gray-700 hover:bg-gray-200/50',
        'focus:ring-gray-500/50',
        'dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700/50',
      ].join(' '),
      primary: [
        'text-primary-500 hover:text-primary-700 hover:bg-primary-200/50',
        'focus:ring-primary-500/50',
        'dark:text-primary-400 dark:hover:text-primary-200 dark:hover:bg-primary-800/50',
      ].join(' '),
      secondary: [
        'text-gray-500 hover:text-gray-700 hover:bg-gray-200/50',
        'focus:ring-gray-500/50',
        'dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700/50',
      ].join(' '),
      success: [
        'text-cyan-500 hover:text-cyan-700 hover:bg-cyan-200/50',
        'focus:ring-cyan-500/50',
        'dark:text-cyan-400 dark:hover:text-cyan-200 dark:hover:bg-cyan-800/50',
      ].join(' '),
      warning: [
        'text-yellow-500 hover:text-yellow-700 hover:bg-yellow-200/50',
        'focus:ring-yellow-500/50',
        'dark:text-yellow-400 dark:hover:text-yellow-200 dark:hover:bg-yellow-800/50',
      ].join(' '),
      error: [
        'text-red-500 hover:text-red-700 hover:bg-red-200/50',
        'focus:ring-red-500/50',
        'dark:text-red-400 dark:hover:text-red-200 dark:hover:bg-red-800/50',
      ].join(' '),
      info: [
        'text-blue-500 hover:text-blue-700 hover:bg-blue-200/50',
        'focus:ring-blue-500/50',
        'dark:text-blue-400 dark:hover:text-blue-200 dark:hover:bg-blue-800/50',
      ].join(' '),
      premium: [
        'text-primary-500 hover:text-primary-700 hover:bg-primary-200/50',
        'focus:ring-primary-500/50',
        'dark:text-primary-400 dark:hover:text-primary-200 dark:hover:bg-primary-800/50',
      ].join(' '),
    },
    size: {
      xs: 'w-3 h-3 p-2.5 -m-2.5 box-content',
      sm: 'w-3.5 h-3.5 p-2 -m-2 box-content',
      md: 'w-4 h-4 p-2 -m-2 box-content',
      lg: 'w-5 h-5 p-1.5 -m-1.5 box-content',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'md',
  },
});

@Component({
  selector: 'ui-chip-glass',
  standalone: true,
  imports: [IconGlassComponent],
  templateUrl: './chip-glass.component.html',
})
export class ChipGlassComponent {
  @Input() label: string = '';
  @Input() variant: ChipVariant = 'default';
  @Input() size: ChipSize = 'md';
  @Input() deletable: boolean = false;
  @Input() clickable: boolean = false;
  @Input() disabled: boolean = false;
  @Input() avatar: string = '';
  @Input() avatarAlt: string = '';
  @Input() icon: string = '';
  @Input() themeSection: ThemeSection = 'global';

  @Output() click = new EventEmitter<void>();
  @Output() delete = new EventEmitter<void>();

  computedChipClasses(): string {
    return chipVariants({
      variant: this.variant,
      size: this.size,
      clickable: this.clickable && !this.disabled ? 'true' : 'false',
    });
  }

  computedDeleteClasses(): string {
    return deleteButtonVariants({
      variant: this.variant,
      size: this.size,
    });
  }

  protected iconSize = computed(() => {
    const sizeMap = {
      xs: 'xs' as const,
      sm: 'xs' as const,
      md: 'sm' as const,
      lg: 'md' as const,
    };
    return sizeMap[this.size];
  });

  protected deleteIconSize = computed(() => {
    const sizeMap = {
      xs: 'xs' as const,
      sm: 'xs' as const,
      md: 'xs' as const,
      lg: 'sm' as const,
    };
    return sizeMap[this.size];
  });

  protected avatarContainerClasses = computed(() => {
    const sizeClasses = {
      xs: '-ml-1 mr-1',
      sm: '-ml-1 mr-1.5',
      md: '-ml-1.5 mr-2',
      lg: '-ml-2 mr-2.5',
    };

    return `flex items-center ${sizeClasses[this.size]}`;
  });

  protected avatarClasses = computed(() => {
    const sizeClasses = {
      xs: 'w-4 h-4',
      sm: 'w-5 h-5',
      md: 'w-6 h-6',
      lg: 'w-8 h-8',
    };

    return `${sizeClasses[this.size]} rounded-full object-cover border border-white/30!`;
  });

  protected handleClick(): void {
    if (this.disabled || !this.clickable) return;
    this.click.emit();
  }

  protected handleDelete(event: Event): void {
    event.stopPropagation();
    if (this.disabled) return;
    this.delete.emit();
  }

  protected handleKeyDown(event: KeyboardEvent): void {
    if (this.disabled) return;

    if ((event.key === 'Enter' || event.key === ' ') && this.clickable) {
      event.preventDefault();
      this.handleClick();
    } else if (event.key === 'Delete' || event.key === 'Backspace') {
      if (this.deletable) {
        event.preventDefault();
        this.handleDelete(event);
      }
    }
  }
}

/**
 * Usage Examples:
 *
 * Basic chip:
 * <ui-chip-glass label="Category"></ui-chip-glass>
 *
 * Deletable chip:
 * <ui-chip-glass
 *   label="Electronics"
 *   [deletable]="true"
 *   (delete)="removeCategory('electronics')"
 * ></ui-chip-glass>
 *
 * Clickable chip:
 * <ui-chip-glass
 *   label="Filter"
 *   [clickable]="true"
 *   (click)="applyFilter()"
 *   variant="primary"
 * ></ui-chip-glass>
 *
 * With icon:
 * <ui-chip-glass
 *   label="Featured"
 *   icon="star"
 *   variant="success"
 * ></ui-chip-glass>
 *
 * With avatar:
 * <ui-chip-glass
 *   label="John Doe"
 *   avatar="/path/to/avatar.jpg"
 *   avatarAlt="John Doe"
 *   [deletable]="true"
 * ></ui-chip-glass>
 *
 * Different variants:
 * <ui-chip-glass label="Default" variant="default"></ui-chip-glass>
 * <ui-chip-glass label="Success" variant="success"></ui-chip-glass>
 * <ui-chip-glass label="Warning" variant="warning"></ui-chip-glass>
 * <ui-chip-glass label="Error" variant="error"></ui-chip-glass>
 * <ui-chip-glass label="Premium" variant="premium"></ui-chip-glass>
 *
 * Different sizes:
 * <ui-chip-glass label="Extra Small" size="xs"></ui-chip-glass>
 * <ui-chip-glass label="Small" size="sm"></ui-chip-glass>
 * <ui-chip-glass label="Medium" size="md"></ui-chip-glass>
 * <ui-chip-glass label="Large" size="lg"></ui-chip-glass>
 *
 * E-commerce examples:
 *
 * Product tags:
 * <div class="flex flex-wrap gap-2">
 *   <ui-chip-glass label="New Arrival" variant="success" icon="sparkles"></ui-chip-glass>
 *   <ui-chip-glass label="Best Seller" variant="primary" icon="trending-up"></ui-chip-glass>
 *   <ui-chip-glass label="Sale" variant="error" icon="tag"></ui-chip-glass>
 *   <ui-chip-glass label="Limited Edition" variant="premium" icon="star"></ui-chip-glass>
 * </div>
 *
 * Category filters:
 * <div class="flex flex-wrap gap-2">
 *   @for (category of selectedCategories; track category) {
 *     <ui-chip-glass
 *       [label]="category.name"
 *       [deletable]="true"
 *       (delete)="removeCategory(category)"
 *       variant="primary"
 *       size="sm"
 *     ></ui-chip-glass>
 *   }
 * </div>
 *
 * Product attributes:
 * <div class="space-y-2">
 *   <div class="flex flex-wrap gap-1">
 *     <ui-chip-glass label="Size: L" variant="secondary" size="xs"></ui-chip-glass>
 *     <ui-chip-glass label="Color: Blue" variant="info" size="xs"></ui-chip-glass>
 *     <ui-chip-glass label="Material: Cotton" variant="default" size="xs"></ui-chip-glass>
 *   </div>
 * </div>
 *
 * Shopping cart tags:
 * <ui-chip-glass
 *   label="Free Shipping"
 *   variant="success"
 *   icon="truck"
 *   size="sm"
 * ></ui-chip-glass>
 *
 * User reviews/ratings:
 * <ui-chip-glass
 *   label="Verified Purchase"
 *   variant="success"
 *   icon="shield-check"
 *   size="xs"
 * ></ui-chip-glass>
 *
 * Status indicators:
 * <ui-chip-glass label="In Stock" variant="success" size="sm"></ui-chip-glass>
 * <ui-chip-glass label="Low Stock" variant="warning" size="sm"></ui-chip-glass>
 * <ui-chip-glass label="Out of Stock" variant="error" size="sm"></ui-chip-glass>
 *
 * Applied filters with removal:
 * <div class="flex flex-wrap gap-2">
 *   <ui-chip-glass
 *     label="Price: $50-$100"
 *     [deletable]="true"
 *     (delete)="removePriceFilter()"
 *     variant="primary"
 *   ></ui-chip-glass>
 *   <ui-chip-glass
 *     label="Brand: Nike"
 *     [deletable]="true"
 *     (delete)="removeBrandFilter('nike')"
 *     variant="primary"
 *   ></ui-chip-glass>
 * </div>
 */
